import type { ReactNode } from "react";
import { format, formatDistanceToNowStrict } from "date-fns";
import { Link } from "react-router-dom";
import { BaseSheet } from "@/components/common/BaseSheet";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Button } from "@/components/ui/button";
import type { AdminSlackApp } from "@/types/admin/slack-apps";
import { SLACK_APP_HEALTH } from "./healthMeta";

const formatWhen = (iso: string | null) =>
  iso ? `${formatDistanceToNowStrict(new Date(iso), { addSuffix: true })} (${format(new Date(iso), "d MMM yyyy")})` : "Never";

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 py-2.5">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="min-w-0 text-sm text-gray-900 sm:text-right break-words">{children}</dd>
    </div>
  );
}

interface SlackAppDetailsSheetProps {
  app: AdminSlackApp | null;
  onClose: () => void;
}

export const SlackAppDetailsSheet = ({ app, onClose }: SlackAppDetailsSheetProps) => {
  if (!app) return null;

  const health = SLACK_APP_HEALTH[app.health];
  const name = app.genie_name ?? "Deleted genie";

  return (
    <BaseSheet
      isOpen
      onClose={onClose}
      title={name}
      description="Read-only. Changes happen in the genie's Share drawer."
      size="lg"
      footer={
        <div className="flex w-full justify-start gap-3 border-t pt-4">
          <Button asChild variant="outline" className="min-h-[44px]">
            <Link to={`/genie-hub/my-genies/${app.agent_id}`}>Open genie</Link>
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
          <div className="rounded-[10px] bg-white p-5">
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${health.badge}`}>{health.label}</span>
            <p className="text-sm text-gray-600 mt-3">{health.hint}</p>
          </div>
        </div>

        {app.last_error && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Last problem</h4>
            <p className="text-sm text-gray-900 break-words">{app.last_error}</p>
            <p className="text-xs text-gray-500 mt-1">{formatWhen(app.last_error_at)}</p>
          </div>
        )}

        <dl className="divide-y divide-gray-100">
          <Detail label="Owner">
            {app.owner_email ? <CopyableEmail email={app.owner_email} className="text-sm" /> : "No owner"}
          </Detail>
          <Detail label="Plan">{app.has_plan ? "Paid or admin" : "No paid plan"}</Detail>
          <Detail label="Slack App ID">{app.slack_app_id ?? "Not saved yet"}</Detail>
          <Detail label="Credentials">{app.has_credentials ? "Saved" : "Not saved yet"}</Detail>
          <Detail label="Last heard from Slack">{formatWhen(app.last_event_at)}</Detail>
          <Detail label="Manifest">
            Version {app.manifest_version} of {app.current_manifest_version}
          </Detail>
          <Detail label="Missing scopes">{app.missing_scopes.length ? app.missing_scopes.join(", ") : "None"}</Detail>
          <Detail label="Started">{formatWhen(app.created_at)}</Detail>
        </dl>

        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Workspaces ({app.workspaces.length})</h4>
          {app.workspaces.length === 0 ? (
            <p className="text-sm text-gray-500">No workspace has this app.</p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
              {app.workspaces.map((workspace) => (
                <li key={workspace.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span className="min-w-0 text-sm font-medium text-gray-900 truncate">
                    {workspace.name ?? "Slack workspace"}
                  </span>
                  <span className="text-xs text-gray-500 shrink-0">
                    {workspace.is_paused ? "Paused" : "Answering"} · added {format(new Date(workspace.created_at), "d MMM yyyy")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </BaseSheet>
  );
};
